import { isDemoMode } from '../lib/env'
import type { SubmissionDraft, UploadAsset } from '../types/submission'

export interface UploadInput {
  submissionId: string
  strainId: string
  asset: UploadAsset
  onProgress: (progress: number) => void
}

export interface SubmissionRepository {
  loadDraft(): Promise<SubmissionDraft | null>
  saveDraft(draft: SubmissionDraft): Promise<{ id: string; ownerUid: string }>
  uploadAsset(input: UploadInput): Promise<Pick<UploadAsset, 'status' | 'storagePath'>>
  removeAsset(submissionId: string, strainId: string, asset: UploadAsset): Promise<void>
  removeStrain(submissionId: string, strainId: string): Promise<void>
  finalize(submissionId: string): Promise<{ submissionId: string }>
}

const LOCAL_DRAFT_KEY = 'ht-nugshots-demo-draft'

class DemoSubmissionRepository implements SubmissionRepository {
  async loadDraft(): Promise<SubmissionDraft | null> {
    const stored = localStorage.getItem(LOCAL_DRAFT_KEY)
    if (!stored) return null
    try {
      const draft = JSON.parse(stored) as SubmissionDraft
      return {
        ...draft,
        step: draft.details.contactEmail ? 2 : 1,
        strains: draft.strains.map((strain) => ({
          ...strain,
          assets: strain.assets.map((asset) => ({
            ...asset,
            previewUrl: '/images/nugshot-hero.jpg',
            progress: 100,
            status: 'ready',
          })),
        })),
      }
    } catch {
      localStorage.removeItem(LOCAL_DRAFT_KEY)
      return null
    }
  }

  async saveDraft(draft: SubmissionDraft): Promise<{ id: string; ownerUid: string }> {
    const id = draft.id ?? 'demo-submission-001'
    const serializable = {
      ...draft,
      id,
      ownerUid: 'demo-contributor',
      strains: draft.strains.map((strain) => ({
        ...strain,
        assets: strain.assets.map((asset) => ({ ...asset, file: undefined })),
      })),
    }
    localStorage.setItem(LOCAL_DRAFT_KEY, JSON.stringify(serializable))
    return { id, ownerUid: 'demo-contributor' }
  }

  async uploadAsset({ asset, onProgress }: UploadInput) {
    for (const progress of [20, 48, 74, 100]) {
      await new Promise((resolve) => window.setTimeout(resolve, 90))
      onProgress(progress)
    }
    await new Promise((resolve) => window.setTimeout(resolve, 180))
    return { status: 'ready' as const, storagePath: `demo/${asset.id}` }
  }

  async removeAsset(): Promise<void> {
    await Promise.resolve()
  }

  async removeStrain(): Promise<void> {
    await Promise.resolve()
  }

  async finalize(submissionId: string): Promise<{ submissionId: string }> {
    await new Promise((resolve) => window.setTimeout(resolve, 500))
    localStorage.removeItem(LOCAL_DRAFT_KEY)
    return { submissionId }
  }
}

let firebaseRepositoryPromise: Promise<SubmissionRepository> | undefined

function getFirebaseRepository(): Promise<SubmissionRepository> {
  firebaseRepositoryPromise ??= import('./firebase-submissions').then(
    ({ firebaseSubmissionRepository }) => firebaseSubmissionRepository,
  )
  return firebaseRepositoryPromise
}

class LazyFirebaseSubmissionRepository implements SubmissionRepository {
  async loadDraft() {
    return (await getFirebaseRepository()).loadDraft()
  }

  async saveDraft(draft: SubmissionDraft) {
    return (await getFirebaseRepository()).saveDraft(draft)
  }

  async uploadAsset(input: UploadInput) {
    return (await getFirebaseRepository()).uploadAsset(input)
  }

  async removeAsset(submissionId: string, strainId: string, asset: UploadAsset) {
    return (await getFirebaseRepository()).removeAsset(submissionId, strainId, asset)
  }

  async removeStrain(submissionId: string, strainId: string) {
    return (await getFirebaseRepository()).removeStrain(submissionId, strainId)
  }

  async finalize(submissionId: string) {
    return (await getFirebaseRepository()).finalize(submissionId)
  }
}

export const submissionRepository: SubmissionRepository = isDemoMode
  ? new DemoSubmissionRepository()
  : new LazyFirebaseSubmissionRepository()
